import React from "react";
import { Link } from "react-router-dom";
import { V2 } from "../../Api";
import { RouteComponentPropsHistory } from "../../types";
import { formatDateTimeLocal, formatDateTimeRelative, formatMimeLong, formatMimeShort, formatSize } from "../../Utilities";
import { IAuthProps } from "../AuthContext";

import withTitle from "../withTitle";

import "../../ticket.css";

interface IFolderViewState {
    isLoading: boolean | string;

    folderInfo: any,

    folders: any[];
    files: any[];
}

interface IFolderViewProps extends RouteComponentPropsHistory<any, any>, IAuthProps { }

const PAGE_SIZE = 100;

class FolderView extends React.Component<IFolderViewProps, IFolderViewState> {
    constructor(props: Readonly<IFolderViewProps>) {
        super(props);

        const state: IFolderViewState = {
            isLoading: true,

            folderInfo: null,

            folders: [],
            files: [],
        };

        this.folderToIcon.bind(this);
        this.fileTypeToIcon.bind(this);

        this.state = state;
    }

    public componentDidMount() {
        this.loadData();
    }

    public componentDidUpdate(prevProps: Readonly<IFolderViewProps>, prevState: Readonly<IFolderViewState>) {
        if (this.props.match.params.id !== prevProps.match.params.id) {
            this.loadData();
        }
    }

    public render() {
        return (
            <div className="viewer-root">
                <header className="ticket-list-header">{this.state.folderInfo?.name || "All Folders"}</header>
                <ul className="ticket-list">
                    {this.state.isLoading === true
                        ? <li><div className="spinner spinner-center lds-dual-ring" /></li>
                        : typeof this.state.isLoading === "string"
                            ? <li><span className="error-text">{this.state.isLoading}</span></li>
                            : <>
                                {this.state.folderInfo
                                    ? <li key={"folder_back"} tabIndex={0}>
                                        <Link to={this.state.folderInfo.parent ? `/folders/${this.state.folderInfo.parent?.id}` : "/folders"}>
                                            <div className="ticket-icon">
                                                {this.folderToIcon("back")}
                                            </div>
                                            <div>
                                                <div className="ticket-title">{this.state.folderInfo.parent?.name || "All Folders"}</div>
                                            </div>
                                        </Link>
                                    </li>
                                    : null}
                                {this.state.folders.length === 0 && this.state.files.length === 0
                                    ? <li><div style={{ margin: "16px" }}>No files or folders to display</div></li>
                                    : <>
                                        {this.state.folders.map(a => <li key={a.id} tabIndex={0}>
                                            <Link to={`/folders/${a.id}`}>
                                                <div className="ticket-icon">
                                                    {this.folderToIcon("")}
                                                </div>
                                                <div>
                                                    <div className="ticket-title">{a.name}</div>
                                                </div>
                                            </Link>
                                        </li>)}
                                        {this.state.files.map(a => <li key={a.id} tabIndex={0}>
                                            <Link to={`/files/${a.id}`}>
                                                <div className="ticket-icon">
                                                    {this.fileTypeToIcon(a.mime)}
                                                </div>
                                                <div>
                                                    <div className="ticket-title">{a.title}</div>
                                                    <div className="ticket-subtext">
                                                        <span>{formatMimeLong(a.mime)}</span>
                                                        <span className="divider">|</span>
                                                        <span>{formatSize(a.contentLength)}</span>
                                                        <span className="divider">|</span>
                                                        <span>{formatDateTimeLocal(a.creationTimestamp)}</span>
                                                    </div>
                                                </div>
                                            </Link>
                                        </li>)}
                                    </>}
                            </>}
                </ul>
            </div>
        );
    }

    private async loadData() {
        this.setState({
            isLoading: true
        });

        const folderId = this.props.match.params.id;

        try {
            const [folderInfo, folders, files] = await Promise.all([
                (folderId
                    ? V2.get<any>(`folders/${folderId}`)
                    : Promise.resolve(null)
                ),
                (async () => {
                    const folders = [];
                    let offset = 0;

                    while (true) {
                        const response = await V2.get<any>(
                            folderId
                                ? `folders/${folderId}/folders?offset=${offset}&limit=${PAGE_SIZE}`
                                : `folders?offset=${offset}&limit=${PAGE_SIZE}`
                        );
                        const subFolders = response.folders as any[];

                        folders.push(...subFolders);

                        if (subFolders.length < PAGE_SIZE)
                            break;
                    }

                    return folders;
                })(),
                (async () => {
                    if (!folderId) {
                        // No files in the root
                        return [];
                    }

                    const files = [];
                    let offset = 0;

                    while (true) {
                        const response = await V2.get<any>(`folders/${folderId}/files?offset=${offset}&limit=${PAGE_SIZE}`);
                        const innerFiles = response.files as any[];

                        files.push(...innerFiles);

                        if (innerFiles.length < PAGE_SIZE)
                            break;
                    }

                    return files;
                })()
            ]);

            this.setState({
                isLoading: false,
                folderInfo,
                folders,
                files,
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Failed to fetch data"
            });
        }
    }

    private fileTypeToIcon(mime: string) {
        switch (mime) {
            case "application/pdf": return <i className="icon-file-pdf-o" />;
            case "application/x-zip":
            case "application/zip": return <i className="icon-file-archive-o" />;
            default:
                if (mime.startsWith("image/"))
                    return <i className="icon-file-image-o" />
                else if (mime.startsWith("video/"))
                    return <i className="icon-file-video-o" />
                else if (mime.startsWith("audio/"))
                    return <i className="icon-file-audio-o" />
                else if (mime.startsWith("text/"))
                    return <i className="icon-file-text-o" />
                else
                    return <i className="icon-file-o" />
        }
    }

    private folderToIcon(type: string) {
        switch (type) {
            case "back": return <i className="icon-arrow-up" />;
            default: return <i className="icon-folder" />;
        }
    }
}

export default withTitle("Files & Folders", FolderView);
